import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, BookOpen, Calendar } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '~/components/ui/card';
import { Badge } from '~/components/ui/badge';
import {
  createSelectColumn,
  DataTable,
  SortableHeader,
} from '~/components/ui/data-table';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { useCoursesQuery } from './useCoursesQuery';
import { useCreateCourse } from './useCreateCourse';
import { useUpdateCourse } from './useUpdateCourse';
import { useDeleteCourse } from './useDeleteCourse';
import { useUpdateCourseTerms } from './useUpdateCourseTerms';
import CourseFormDialog from './CourseFormDialog';
import type { CourseFromQuery, TermFromQuery } from './coursesTypes';
import { schoolYearSettingsPath } from '~/pages/SchoolSettings/schoolYearSettingsPath';

interface CoursesSectionProps {
  schoolYearId: string;
  onCourseClick?: (course: CourseFromQuery) => void;
}

export default function CoursesSection({ schoolYearId, onCourseClick }: CoursesSectionProps) {
  const navigate = useNavigate();
  const { schoolSlug, yearSlug } = useParams();
  const { data, loading, refetch } = useCoursesQuery(schoolYearId);
  const [createCourse] = useCreateCourse();
  const [updateCourse] = useUpdateCourse();
  const [deleteCourse] = useDeleteCourse();
  const [updateCourseTerms] = useUpdateCourseTerms();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<CourseFromQuery | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<CourseFromQuery | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [serverBlockers, setServerBlockers] = useState<Array<{
    id: string;
    displayName: string;
    section: string;
    term: { id: string; name: string };
  }>>([]);
  const [deleting, setDeleting] = useState(false);

  const courses = useMemo(
    () => (data?.schoolYear?.courses ?? []) as CourseFromQuery[],
    [data]
  );

  const availableTerms = useMemo(
    () => (data?.schoolYear?.terms ?? []) as TermFromQuery[],
    [data]
  );

  const totalTerms = availableTerms.length;

  const availableGradeLevels = useMemo(
    () =>
      ([...(data?.schoolYear?.school?.gradeLevelsSetting?.items ?? [])] as any[])
        .sort((a, b) => ((a as { order?: number }).order ?? 0) - ((b as { order?: number }).order ?? 0))
        .map((g) => (g as { description: string }).description),
    [data]
  );

  const handleCreate = useCallback(() => {
    setEditingCourse(null);
    setDialogOpen(true);
  }, []);

  const handleEdit = useCallback((course: CourseFromQuery) => {
    setEditingCourse(course);
    setDialogOpen(true);
  }, []);

  const handleConfigureTerms = useCallback(() => {
    setDialogOpen(false);
    if (schoolSlug && yearSlug) {
      navigate(schoolYearSettingsPath(schoolSlug, yearSlug));
    }
  }, [navigate, schoolSlug, yearSlug]);

  const handleSave = useCallback(
    async (formData: any) => {
      if (formData.id) {
        // Update existing course
        const updateResult = await updateCourse({
          variables: {
            input: {
              id: formData.id,
              name: formData.name,
              subtitle: formData.subtitle || null,
              catalogCode: formData.catalogCode || null,
              description: formData.description || null,
              defaultSections: formData.defaultSections,
              gradeLevelKeys: formData.gradeLevelKeys,
            },
          },
        });
        const updateErrors = updateResult.data?.updateCourse?.userErrors;
        if (updateErrors?.length) {
          throw new Error(updateErrors.map((error) => error.message).join(', '));
        }

        // Update term associations
        const termsResult = await updateCourseTerms({
          variables: {
            input: {
              courseId: formData.id,
              termIds: formData.termIds,
            },
          },
        });
        const termErrors = termsResult.data?.updateCourseTerms?.userErrors;
        if (termErrors?.length) {
          throw new Error(termErrors.map((error) => error.message).join(' '));
        }
      } else {
        // Create new course
        const createResult = await createCourse({
          variables: {
            input: {
              schoolYearId,
              name: formData.name,
              subtitle: formData.subtitle || null,
              catalogCode: formData.catalogCode || null,
              description: formData.description || null,
              defaultSections: formData.defaultSections,
              gradeLevelKeys: formData.gradeLevelKeys,
              termIds: formData.termIds,
            },
          },
        });
        const createErrors = createResult.data?.createCourse?.userErrors;
        if (createErrors?.length) {
          throw new Error(createErrors.map((error) => error.message).join(', '));
        }
      }
      await refetch();
    },
    [schoolYearId, createCourse, updateCourse, updateCourseTerms, refetch]
  );

  const handleDelete = useCallback(async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const result = await deleteCourse({ variables: { input: { id: deleteConfirm.id } } });
      const payload = result.data?.deleteCourse;
      if (!payload?.success) {
        setDeleteError(payload?.userErrors?.map((error) => error.message).join(' ') || 'Unable to delete the course.');
        setServerBlockers([...(payload?.blockingClassOfferings ?? [])]);
        return;
      }

      setDeleteConfirm(null);
      setServerBlockers([]);
      await refetch();
    } finally {
      setDeleting(false);
    }
  }, [deleteConfirm, deleteCourse, refetch]);

  const openDeleteConfirmation = useCallback((course: CourseFromQuery) => {
    setDeleteError(null);
    setServerBlockers([]);
    setDeleteConfirm(course);
  }, []);

  const closeDeleteConfirmation = useCallback(() => {
    setDeleteConfirm(null);
    setDeleteError(null);
    setServerBlockers([]);
  }, []);

  const openClassOffering = useCallback((course: CourseFromQuery, offeringId: string) => {
    if (!schoolSlug || !yearSlug) return;
    closeDeleteConfirmation();
    navigate(`/app/${schoolSlug}/years/${yearSlug}/courses/${course.slug}/classes/${offeringId}`);
  }, [closeDeleteConfirmation, navigate, schoolSlug, yearSlug]);

  const columns = useMemo<ColumnDef<CourseFromQuery>[]>(() => [
    createSelectColumn<CourseFromQuery>(),
    {
      id: 'search',
      accessorFn: (course) => [
        course.name,
        course.subtitle,
        course.catalogCode,
        ...course.gradeLevelKeys,
      ].filter(Boolean).join(' '),
      header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
      meta: { sortKey: (course: CourseFromQuery) => course.name },
      cell: ({ row }) => (
        <div>
          <span className="font-medium text-primary underline-offset-4 hover:underline">
            {row.original.name}
          </span>
          {row.original.subtitle && (
            <span className="ml-1 text-muted-foreground">— {row.original.subtitle}</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'catalogCode',
      header: 'Code',
      cell: ({ row }) => row.original.catalogCode || '—',
    },
    {
      id: 'gradeLevels',
      header: 'Grade Levels',
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.gradeLevelKeys.length > 0
            ? row.original.gradeLevelKeys.map((gradeLevel) => (
                <Badge key={gradeLevel} variant="secondary" className="text-xs">
                  {gradeLevel}
                </Badge>
              ))
            : <span className="text-muted-foreground">—</span>}
        </div>
      ),
      meta: {
        facet: {
          title: 'Grade',
          getKey: (course: CourseFromQuery) =>
            course.gradeLevelKeys.length > 0 ? course.gradeLevelKeys : ['NO_GRADE_LEVEL'],
          getLabel: (_course: CourseFromQuery, key: string) =>
            key === 'NO_GRADE_LEVEL' ? 'No grade level' : key,
        },
      },
    },
    {
      id: 'terms',
      accessorFn: (course) => course.terms.length,
      header: 'Terms',
      cell: ({ row }) => `${row.original.terms.length}/${totalTerms}`,
      meta: {
        facet: {
          title: 'Terms',
          getKey: (course: CourseFromQuery) => {
            if (course.terms.length === 0) return 'NO_TERMS';
            if (course.terms.length === totalTerms) return 'ALL_TERMS';
            return 'SOME_TERMS';
          },
          getLabel: (_course: CourseFromQuery, key: string) => ({
            NO_TERMS: 'No terms',
            SOME_TERMS: 'Some terms',
            ALL_TERMS: 'All terms',
          }[key] ?? key),
        },
      },
    },
    {
      accessorKey: 'defaultSections',
      header: 'Sections',
      cell: ({ row }) => row.original.defaultSections,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex justify-end gap-1" data-no-row-click>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label={`Edit ${row.original.name}`}
            onClick={() => handleEdit(row.original)}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            aria-label={`Delete ${row.original.name}`}
            onClick={() => openDeleteConfirmation(row.original)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
  ], [handleEdit, openDeleteConfirmation, totalTerms]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Loading courses...
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Courses
              </CardTitle>
              <CardDescription>
                {courses.length} course{courses.length !== 1 ? 's' : ''} configured
              </CardDescription>
            </div>
            <Button size="sm" onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-1" />
              Add Course
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {availableTerms.length === 0 && (
            <div className="mb-4 flex flex-col gap-3 rounded-lg border border-dashed p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-medium">Configure academic terms before creating classes</p>
                <p className="text-sm text-muted-foreground">
                  Courses can be drafted now, but they need at least one term before class instances can be created.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={handleConfigureTerms}>
                <Calendar className="mr-1 h-4 w-4" />
                Configure Terms
              </Button>
            </div>
          )}
          {courses.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center">
              <p className="text-muted-foreground">No courses yet. Add your first course to get started.</p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={courses}
              enableRowSelection
              searchColumn="search"
              searchPlaceholder="Search courses..."
              defaultSorting={[{ id: 'search', desc: false }]}
              stateKey={`courses-${schoolYearId}`}
              onRowClick={onCourseClick}
            />
          )}
        </CardContent>
      </Card>

      <CourseFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSave={handleSave}
        course={editingCourse}
        availableTerms={availableTerms}
        availableGradeLevels={availableGradeLevels}
        onConfigureTerms={handleConfigureTerms}
      />

      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) closeDeleteConfirmation(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {(serverBlockers.length > 0 || (deleteConfirm?.classOfferings.length ?? 0) > 0)
                ? 'Remove class instances first'
                : 'Delete Course'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {(serverBlockers.length > 0 || (deleteConfirm?.classOfferings.length ?? 0) > 0)
                ? `“${deleteConfirm?.name}” still has class instances. Open each class below and remove it before deleting the course.`
                : `Are you sure you want to delete “${deleteConfirm?.name}”? This will also remove its term selections. This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {deleteConfirm && (() => {
            const blockers = serverBlockers.length > 0 ? serverBlockers : deleteConfirm.classOfferings;
            if (blockers.length === 0) return null;

            return (
              <div className="space-y-2 rounded-md border p-3">
                {blockers.map((offering) => (
                  <button
                    type="button"
                    key={offering.id}
                    className="block w-full rounded px-2 py-1.5 text-left text-sm text-primary hover:bg-muted hover:underline"
                    onClick={() => openClassOffering(deleteConfirm, offering.id)}
                  >
                    {offering.displayName}
                  </button>
                ))}
              </div>
            );
          })()}

          {deleteError && (
            <div role="alert" className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {deleteError}
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>
              {(serverBlockers.length > 0 || (deleteConfirm?.classOfferings.length ?? 0) > 0) ? 'Close' : 'Cancel'}
            </AlertDialogCancel>
            {serverBlockers.length === 0 && (deleteConfirm?.classOfferings.length ?? 0) === 0 && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
